import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Typography,
  Descriptions,
  Badge,
  Tag,
  List,
  Avatar,
  Space,
  Divider,
  Progress,
  Statistic,
  Input,
  Select,
  Empty,
  Spin
} from 'antd';
import {
  ArrowLeftOutlined,
  CrownOutlined,
  CheckCircleOutlined,
  UserOutlined,
  BankOutlined,
  SearchOutlined,
  DollarOutlined,
  CalendarOutlined,
  StarOutlined,
  DatabaseOutlined,
  ApiOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { subscriptionPlansService } from '../services/subscriptionPlansService';
import { organizationService } from '../services/organizationService';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const SubscriptionPlanDetail = ({ planId, onBack }) => {
  const [plan, setPlan] = useState(null);
  const [assignedOrganizations, setAssignedOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgFilters, setOrgFilters] = useState({
    search: '',
    status: 'all'
  });

  // Fetch plan details
  const fetchPlan = async () => {
    setLoading(true);
    try {
      const response = await subscriptionPlansService.getPlan(planId);
      if (response.success) {
        setPlan(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch plan:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch organizations assigned to this plan
  const fetchAssignedOrganizations = async () => {
    setOrgLoading(true);
    try {
      // Get all organizations and filter by plan
      const response = await organizationService.getOrganizations({
        limit: 100 // Get more organizations to filter
      });
      if (response.success) {
        // Filter organizations that match this plan
        const filtered = response.data.organizations.filter(org => {
          const matchesPlan = org.plan === plan?.name?.toLowerCase().replace(' plan', '');
          const matchesSearch = orgFilters.search ? 
            org.name.toLowerCase().includes(orgFilters.search.toLowerCase()) ||
            org.contact.email.toLowerCase().includes(orgFilters.search.toLowerCase()) : true;
          const matchesStatus = orgFilters.status === 'all' ? true : 
            (orgFilters.status === 'active' ? org.isActive : !org.isActive);
          
          return matchesPlan && matchesSearch && matchesStatus;
        });
        setAssignedOrganizations(filtered);
      }
    } catch (error) {
      console.error('Failed to fetch assigned organizations:', error);
      setAssignedOrganizations([]);
    } finally {
      setOrgLoading(false);
    }
  };

  useEffect(() => {
    if (planId) {
      fetchPlan();
    }
  }, [planId]);

  useEffect(() => {
    if (plan) {
      fetchAssignedOrganizations();
    }
  }, [plan, orgFilters]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Empty description="Plan not found" />
      </div>
    );
  }

  // Calculate usage statistics
  const totalAssigned = assignedOrganizations.length;
  const activeOrgs = assignedOrganizations.filter(org => org.isActive).length;
  const totalUsers = assignedOrganizations.reduce((sum, org) => sum + (org.stats?.totalUsers || 0), 0);
  const totalRevenue = totalAssigned * plan.price;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={onBack}
          style={{ marginBottom: 16 }}
        >
          Back to Plans
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          <Space>
            <Avatar 
              style={{ backgroundColor: plan.metadata?.color || '#1890ff' }}
              icon={plan.metadata?.icon ? <span>{plan.metadata.icon}</span> : <CrownOutlined />}
            />
            {plan.name}
            {plan.isPopular && (
              <Tag color="gold">
                <StarOutlined /> Popular
              </Tag>
            )}
            <Badge
              status={plan.isActive ? 'success' : 'error'}
              text={plan.isActive ? 'Active' : 'Inactive'}
            />
          </Space>
        </Title>
        <Text type="secondary">{plan.description}</Text>
      </div>

      <Row gutter={24}>
        {/* Column 1: Subscription Details */}
        <Col span={12}>
          <Card title="Subscription Details" style={{ height: '100%' }}>
            {/* Pricing Information */}
            <div style={{ marginBottom: 24 }}>
              <Statistic
                title="Price"
                value={plan.price}
                prefix="₹"
                formatter={(v) => Number(v || 0).toLocaleString('en-IN')}
                suffix={`/ ${plan.billingPeriod}`}
                valueStyle={{ 
                  color: plan.price === 0 ? '#52c41a' : '#1890ff',
                  fontSize: '32px'
                }}
              />
            </div>

            {/* Plan Information */}
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Plan Name">
                {plan.name}
              </Descriptions.Item>
              <Descriptions.Item label="Billing Period">
                <Tag color={
                  plan.billingPeriod === 'monthly' ? 'blue' : 
                  plan.billingPeriod === 'yearly' ? 'green' : 
                  'purple'
                }>
                  <CalendarOutlined /> {plan.billingPeriod.charAt(0).toUpperCase() + plan.billingPeriod.slice(1)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge
                  status={plan.isActive ? 'success' : 'error'}
                  text={plan.isActive ? 'Active' : 'Inactive'}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Popular Plan">
                {plan.isPopular ? (
                  <Tag color="gold"><StarOutlined /> Yes</Tag>
                ) : (
                  <Tag>No</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Plan Limits</Divider>
            
            {/* Plan Limits */}
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="Max Users"
                    value={plan.limits?.maxUsers === -1 ? '∞' : plan.limits?.maxUsers || 0}
                    prefix={<UserOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="Max Storage"
                    value={plan.limits?.maxStorage === -1 ? '∞' : `${plan.limits?.maxStorage || 0}GB`}
                    prefix={<DatabaseOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="Max Leads"
                    value={plan.limits?.maxLeads === -1 ? '∞' : plan.limits?.maxLeads || 0}
                    prefix={<TeamOutlined />}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="Max Integrations"
                    value={plan.limits?.maxIntegrations === -1 ? '∞' : plan.limits?.maxIntegrations || 0}
                    prefix={<ApiOutlined />}
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
            </Row>

            <Divider>Features Included</Divider>

            {/* Features List */}
            <List
              dataSource={plan.features || []}
              renderItem={(feature) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                    title={feature.name}
                    description={feature.description}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* Column 2: Assigned Organizations */}
        <Col span={12}>
          <Card 
            title={
              <Space>
                <BankOutlined />
                Assigned Organizations ({totalAssigned})
              </Space>
            }
            extra={
              <Space>
                <Text type="secondary">Revenue: ₹{Number(totalRevenue || 0).toLocaleString('en-IN')}/month</Text>
              </Space>
            }
            style={{ height: '100%' }}
          >
            {/* Usage Statistics */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic
                  title="Organizations"
                  value={totalAssigned}
                  prefix={<BankOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Active Orgs"
                  value={activeOrgs}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Total Users"
                  value={totalUsers}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
            </Row>

            {/* Filters */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={16}>
                <Input
                  placeholder="Search organizations..."
                  prefix={<SearchOutlined />}
                  value={orgFilters.search}
                  onChange={(e) => setOrgFilters(prev => ({ ...prev, search: e.target.value }))}
                  allowClear
                />
              </Col>
              <Col span={8}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Status"
                  value={orgFilters.status}
                  onChange={(value) => setOrgFilters(prev => ({ ...prev, status: value }))}
                >
                  <Option value="all">All Status</Option>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Col>
            </Row>

            {/* Organizations List */}
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {orgLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin />
                </div>
              ) : assignedOrganizations.length > 0 ? (
                <List
                  dataSource={assignedOrganizations}
                  renderItem={(org) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Avatar 
                            style={{ backgroundColor: '#1890ff' }}
                            icon={<BankOutlined />}
                          />
                        }
                        title={
                          <Space>
                            {org.name}
                            <Badge
                              status={org.isActive ? 'success' : 'error'}
                              text={org.isActive ? 'Active' : 'Inactive'}
                            />
                          </Space>
                        }
                        description={
                          <div>
                            <div>{org.contact.email}</div>
                            <Text type="secondary">
                              {org.stats?.totalUsers || 0} users • 
                              Created {new Date(org.createdAt).toLocaleDateString()}
                            </Text>
                          </div>
                        }
                      />
                      <div>
                        <Text strong>₹{Number(plan.price || 0).toLocaleString('en-IN')}</Text>
                        <br />
                        <Text type="secondary">per month</Text>
                      </div>
                    </List.Item>
                  )}
                />
              ) : (
                <Empty 
                  description="No organizations assigned to this plan"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SubscriptionPlanDetail;
